import React, { useState, useCallback } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { Box, Grid, Typography } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InfoIcon from '@mui/icons-material/Info';
import uploadIcon from 'src/Assets/Icons/upload.svg';
import Functions from '../../../../Helpers/Functions';
import ImageRepository from '../../../../Repositories/ImageRepository';
import TeachrRepository from '../../../../Repositories/TeachrRepository';
import { userTeachrSetAccountAction } from '../../../../Redux/Actions/UserActions';
import { useProfilContext } from '../../../../Contexts/ProfilContexte';
import useTailwindBrand from '../../../../Theme/useTailwindBrand';
import ButtonField from '../../../../Components/Ui/ButtonField';
import Settings from '../../../../Components/Settings';
import { createImageRef, createTeachrAddress, prepareTeachrForUpdate, updateTeachrData } from 'src/Utils/Function';
import { useSelector } from 'react-redux';
import Teachr from 'src/Objects/models/Teachr';

const FILE_TYPES = ['PDF', 'PNG', 'JPEG', 'JPG'];

interface SapDocumentUploadStepProps {
    onPrevious: () => void;
    onNext: () => void;
}

const SapDocumentUploadStep: React.FC<SapDocumentUploadStepProps> = ({ onPrevious, onNext }) => {
    const { teachr, dispatch, currentProcuration } = useProfilContext();
    const { classes, common, brandConfig } = useTailwindBrand();
    const primaryHex = brandConfig.colors?.primary ?? '#1976d2';
    const isNova = currentProcuration === 40;
    const formData = useSelector((state: any) => state.user?.sapFormData);

    const [file, setFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [globalError, setGlobalError] = useState<string | null>(null);

    const handleFileChange = useCallback((f: File) => {
        if (!f) return;
        setFileError(false);
        if (f.type.startsWith('image/')) {
            const ext = f.type.split('/')[1];
            Functions.resizeFile(f, true, ext)
                .then((resized: any) => setFile(resized))
                .catch(() => setFile(f));
        } else {
            setFile(f);
        }
    }, []);

    const handleSubmit = useCallback(async () => {
        const hasDoc = file || teachr?.proofSignSap;
        if (!hasDoc) {
            setFileError(true);
            return;
        }
        if (isNova) {
            if (teachr?.proofSignSap && teachr?.proofSignSap.fileName !== "Declaration_SAP") {
                setGlobalError('Le document déjà en place n\'est pas le bon. Veuillez en uploader un nouveau.');
                setFileError(true);
                return;
            }
        } else {
            if (teachr?.proofSignSap && teachr?.proofSignSap.fileName !== "KBIS_pour_Procuration") {
                setGlobalError('Le document déjà en place n\'est pas le bon. Veuillez en uploader un nouveau.');
                setFileError(true);
                return;
            }
        }

        setLoading(true);
        setGlobalError(null);
        try {
            const updatedTeachr = { ...teachr } as Teachr;

            if (file) {
                if (isNova) {
                    updatedTeachr.proofSignSap = await createImageRef(file,'proofissignSap');  
                } else {
                    updatedTeachr.proofIdentity = await createImageRef(file, 'proof-kbis-sap');
                }
            }
            // const addressIri = (formData?.address && teachr.id && teachr['@id'])
            //     ? await createTeachrAddress(formData.address, teachr['@id'] as string, teachr.id)
            //     : null;

            // if (addressIri) updatedTeachr.address = addressIri;
            prepareTeachrForUpdate(updatedTeachr, {});
            await updateTeachrData(updatedTeachr, dispatch, setLoading);
            onNext();

        } catch (err : any) {
            console.warn('Erreur lors de l\'upload des documents:', err);
            const desc = err?._description ?? '';
            if (desc.includes("exceeds your upload_max_filesize ini directive (limit is 2048 KiB)")) {
                setGlobalError("⚠️ Votre fichier est trop volumineux,\n Le fichier que vous tentez d'envoyer dépasse la taille maximale autorisée de 2 Mo.\n Pour finaliser votre inscription, merci de compresser ou réduire la taille de votre document avant de le renvoyer.");
            } else if (desc.includes("The file size must not exceed 5 MB") || desc.includes("The file size must not exceed 5 mbits")) {
                setGlobalError("⚠️ Votre fichier est trop volumineux.\n La taille maximale autorisée est de 5 Mo. Veuillez compresser ou réduire la taille de votre document avant de le renvoyer.");
            } else if (desc.includes("Invalid or unsupported image format")) {
                setGlobalError("⚠️ Format de fichier invalide.\n Veuillez utiliser un fichier au format JPEG, PNG, GIF ou WebP. Les fichiers PDF ne sont pas acceptés pour les images.");
            } else if (desc.includes('"file" is required')) {
                setGlobalError("⚠️ Aucun fichier sélectionné.\n Veuillez sélectionner un document avant de soumettre.");
            } else {
                setGlobalError('Une erreur est survenue lors de l\'envoi des documents. Veuillez réessayer.');
            }
        } finally {
            setLoading(false);
        }
    }, [file, teachr, isNova, dispatch, onNext]);

    const UploadArea = () => (
        <FileUploader
            classes="focus-within:bg-blue-50"
            hoverTitle="Déposez votre fichier ici"
            onTypeError={() => setFileError(true)}
            handleChange={handleFileChange}
            name="kbisFile"
            types={FILE_TYPES}
        >
            <div className="flex flex-col lg:flex-row items-center justify-center gap-4 py-6">
                <img src={uploadIcon} alt="Upload" className="w-12 h-12 flex-shrink-0" />
                <div className="flex flex-col items-center lg:items-start gap-1">
                    <p className={`text-sm font-semibold text-center lg:text-left ${fileError ? classes.error : classes.text} ${classes.fontFamily}`}>
                        Faites glisser votre fichier ou choisissez{' '}
                        <span className={`font-bold cursor-pointer hover:underline ${fileError ? classes.error : classes.primaryText}`}>
                            un fichier
                        </span>
                    </p>
                    <p className={`text-xs ${fileError ? classes.error : classes.text}`}>
                        Formats acceptés : PDF, PNG, JPEG, JPG
                    </p>
                </div>
            </div>
        </FileUploader>
    );

    return (
        <Settings
            title={isNova ? 'Arrêté préfectoral NOVA' : "Justificatif d'immatriculation"}
            subtitle={isNova ? 'Arrêté préfectoral délivré par NOVA à la suite de votre déclaration' : 'Fournissez votre extrait KBIS ou votre avis de situation SIREN'}
            height="100%"
        >
            <Grid item xs={12}>
                <div className="space-y-5 w-full">
                    {/* Message d'information */}
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: `${primaryHex}40`,
                            bgcolor: `${primaryHex}0d`,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                        }}
                    >
                        <InfoIcon sx={{ color: primaryHex, fontSize: 22, flexShrink: 0, mt: 0.25 }} />
                        <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: brandConfig.fontFamily }}>
                            {isNova
                                ? "Merci de bien vouloir joindre votre arrêté préfectoral délivré par NOVA à la suite de votre déclaration."
                                : <>
                                    Merci de bien vouloir joindre votre extrait KBIS ou votre avis de situation SIREN.
                                    Si vous ne l'avez plus, vous pouvez le retélécharger depuis{' '}
                                    <a
                                        href="https://avis-situation-sirene.insee.fr/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-semibold underline"
                                        style={{ color: primaryHex }}
                                    >
                                        avis-situation-sirene.insee.fr
                                    </a>
                                </>
                            }
                        </Typography>
                    </Box>

                    {/* Zone de dépôt */}
                    <div className="p-4 border border-gray-200 rounded-xl bg-gray-50 space-y-3">
                        <div className="flex items-start gap-3">
                            <div
                                className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: `${primaryHex}1a` }}
                            >
                                <FolderOpenIcon style={{ color: primaryHex, fontSize: 20 }} />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <p className={`font-bold text-gray-900 ${classes.fontFamily}`}>
                                        {isNova ? 'Arrêté préfectoral NOVA' : 'Extrait KBIS ou avis de situation SIREN'}
                                    </p>
                                    <span className="text-xs font-semibold text-red-500 bg-red-50 px-2 py-0.5 rounded-full">
                                        Requis
                                    </span>
                                </div>
                                <p className={`text-xs text-gray-500 mt-0.5 ${classes.fontFamily}`}>
                                    {isNova ? 'Arrêté préfectoral délivré par NOVA à la suite de votre déclaration' : "Document d'immatriculation de votre entreprise"}
                                </p>
                            </div>
                        </div>

                        {file ? (
                            <div className="bg-green-50 border-2 border-dashed border-green-300 rounded-lg px-3 py-2">
                                <div className="flex items-center justify-between gap-4 p-4 bg-white rounded-lg shadow-sm">
                                    <div className="flex items-center gap-3">
                                        <DescriptionIcon className={`w-7 h-7 ${classes.primaryText}`} />
                                        <span className={`font-semibold text-sm ${classes.primaryText} ${classes.fontFamily}`}>
                                            {file.name}
                                        </span>
                                        <CheckCircleIcon className="w-5 h-5 text-green-600" />
                                    </div>
                                    <button
                                        onClick={() => { setFile(null); setFileError(false); }}
                                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                        <DeleteIcon className="w-4 h-4" />
                                        Supprimer
                                    </button>
                                </div>
                            </div>
                        ) : teachr?.proofSignSap ? (
                            <div className="space-y-2">
                                <div className="bg-green-50 border-2 border-dashed border-green-300 rounded-lg p-3 flex items-center gap-3">
                                    <DescriptionIcon className={`w-7 h-7 ${classes.primaryText}`} />
                                    <a
                                        href={teachr.proofSignSap.contentUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`font-semibold text-sm ${classes.primaryText} hover:underline ${classes.fontFamily}`}
                                    >
                                        Document existant
                                    </a>
                                    <CheckCircleIcon className="w-5 h-5 text-green-600 ml-auto" />
                                </div>
                                <p className={`text-xs text-center text-gray-500 ${common?.body ?? ''}`}>
                                    Ou remplacer par un nouveau fichier
                                </p>
                                <div className={`bg-gray-50 border-2 border-dashed rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all ${fileError ? 'border-red-300' : 'border-gray-300'}`}>
                                    <UploadArea />
                                </div>
                            </div>
                        ) : (
                            <div className={`border-2 border-dashed rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all ${fileError ? 'bg-red-50 border-red-300' : 'bg-white border-gray-300'}`}>
                                <UploadArea />
                            </div>
                        )}
                    </div>

                    {globalError && (
                        <p className={`text-sm text-red-600 text-center ${classes.fontFamily}`}>{globalError}</p>
                    )}

                    <div className="flex justify-between pt-2">
                        <ButtonField
                            label="Précédent"
                            variant="outlined"
                            className="w-full font-bold lg:w-auto"
                            onClick={onPrevious}
                            disabled={loading}
                        />
                        <ButtonField
                            label="Suivant"
                            variant="contained"
                            className="w-full font-bold lg:w-auto"
                            onClick={handleSubmit}
                            disabled={loading}
                        />
                    </div>
                </div>
            </Grid>
        </Settings>
    );
};

export default SapDocumentUploadStep;
